import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useAccess } from "../context/AccessContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Crown, Send, Sparkles, Plus, Trash2, Image as ImageIcon, X, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import MarkdownMessage from "../components/MarkdownMessage";

function Paywall() {
  const navigate = useNavigate();
  return (
    <div className="grid place-items-center py-20" data-testid="ai-paywall">
      <div className="max-w-md rounded-3xl border border-amber-500/30 bg-gradient-to-b from-amber-500/10 to-transparent p-8 text-center">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-amber-500 text-white"><Sparkles size={22}/></div>
        <h2 className="mt-4 font-heading text-2xl font-semibold">Syllox AI is Premium</h2>
        <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">Upgrade to Premium to unlock Syllox AI.</p>
        <p className="mt-2 text-xs font-medium text-amber-600 dark:text-amber-400" data-testid="ai-paywall-pricing">₹49/month · ₹299/year</p>
        <Button onClick={() => navigate("/premium")} className="mt-6 bg-gradient-to-r from-amber-400 to-orange-500 text-white" data-testid="ai-upsell-btn">Upgrade to Premium</Button>
      </div>
    </div>
  );
}

export default function SylloxAI() {
  const { user } = useAuth();
  const { effectivePremium } = useAccess();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [image, setImage] = useState(null); // data URL
  const [thinking, setThinking] = useState(false);
  const [quota, setQuota] = useState(null);
  // Same natural-scroll model as Messages: drive scrollTop on the list
  // container, only auto-scroll when the user was already near the bottom
  // and a genuinely new message (or the thinking indicator toggling on)
  // arrived. Reading old Q/A never yanks the view down.
  const listRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const prevSessionIdRef = useRef(null);
  const prevLastKeyRef = useRef(null);

  const loadSessions = useCallback(async () => {
    const { data } = await api.get("/ai/sessions");
    setSessions(data);
  }, []);

  const loadMessages = useCallback(async (sid) => {
    const { data } = await api.get(`/ai/messages/${sid}`);
    setMessages(data);
  }, []);

  const loadQuota = useCallback(async () => {
    try {
      const { data } = await api.get("/ai/quota");
      setQuota(data);
    } catch (err) {
      // Non-fatal: badge just stays hidden. Log only in dev.
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn("Failed to load AI quota", err);
      }
    }
  }, []);

  useEffect(() => { if (effectivePremium) { loadSessions(); loadQuota(); } }, [effectivePremium, loadSessions, loadQuota]);
  useEffect(() => { if (sessionId) loadMessages(sessionId); }, [sessionId, loadMessages]);

  // Reset scroll state on session switch — a brand new session should
  // always start at the bottom regardless of where we were.
  useEffect(() => {
    if (sessionId !== prevSessionIdRef.current) {
      prevSessionIdRef.current = sessionId;
      isNearBottomRef.current = true;
      prevLastKeyRef.current = null;
    }
  }, [sessionId]);

  const handleListScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    isNearBottomRef.current = dist < 120;
  }, []);

  // Auto-scroll only when (a) user was near the bottom AND (b) something
  // genuinely new appeared (new last message OR the typing indicator just
  // turned on). Prevents the render triggered by /ai/messages refetch from
  // yanking the user down while they read older answers.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const lastId = messages.length ? messages[messages.length - 1].message_id : null;
    const key = `${lastId}|${thinking ? 1 : 0}`;
    const changed = key !== prevLastKeyRef.current;
    prevLastKeyRef.current = key;
    if (!changed) return;
    if (!isNearBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, thinking]);

  if (!effectivePremium) return <Paywall />;

  const newChat = () => { setSessionId(null); setMessages([]); };

  const pickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2_500_000) { toast.error("Image too large (max 2MB)"); return; }
    const r = new FileReader();
    r.onload = () => setImage(r.result);
    r.readAsDataURL(f);
  };

  const send = async (e) => {
    e?.preventDefault();
    const content = text.trim();
    if (!content || thinking) return;
    setText(""); setThinking(true);
    // Sending my own question always jumps to the bottom, matching WhatsApp/
    // ChatGPT: force the near-bottom flag so the auto-scroll effect fires
    // even if we were scrolled up while composing.
    isNearBottomRef.current = true;
    const userMsg = { message_id: `tmp_${Date.now()}`, role: "user", content, has_image: !!image, created_at: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    const imgB64 = image;
    setImage(null);
    try {
      const { data } = await api.post("/ai/chat", {
        session_id: sessionId,
        text: content,
        image_base64: imgB64 || null,
      }, {
        // Backend has a 75s hard timeout on the LLM call plus its own retry;
        // give the whole round-trip 90s before we bail on the client, so
        // the user gets a clean toast instead of a hanging spinner.
        timeout: 90_000,
      });
      if (!sessionId) { setSessionId(data.session_id); loadSessions(); }
      setMessages((m) => [...m, { message_id: `r_${Date.now()}`, role: "assistant", content: data.reply, created_at: new Date().toISOString() }]);
      if (data.quota) setQuota(data.quota);
    } catch (err) {
      // Roll the user's message OUT of the transcript on failure so their
      // input is restored to the composer (see below). Otherwise the failed
      // question sits stranded above the composer forever and the user
      // has no way to retry without re-typing.
      setMessages((m) => m.filter((x) => x.message_id !== userMsg.message_id));
      setText(content);   // restore the composer with what they typed
      const detail = err?.response?.data?.detail;
      const isTimeout = err?.code === "ECONNABORTED" || err?.response?.status === 504;
      toast.error(
        detail
          || (isTimeout ? "Syllox AI took too long to respond. Try a shorter question." : "AI failed. Please try again.")
      );
      if (err?.response?.status === 429) loadQuota();
    } finally {
      setThinking(false);
    }
  };

  const removeSession = async (sid) => {
    await api.delete(`/ai/sessions/${sid}`);
    if (sid === sessionId) newChat();
    loadSessions();
  };

  return (
    <div className="grid h-[calc(100vh-8rem)] grid-cols-1 overflow-hidden rounded-2xl border border-zinc-200 bg-white md:grid-cols-[280px_1fr] dark:border-zinc-800 dark:bg-zinc-900" data-testid="ai-root">
      <aside className="flex flex-col border-r border-zinc-200 dark:border-zinc-800">
        <div className="flex items-center justify-between border-b border-zinc-200 p-3 dark:border-zinc-800">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/dashboard")}
              className="-ml-1 rounded-md p-1 text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
              aria-label="Back to Dashboard"
              data-testid="ai-back-btn"
            >
              <ArrowLeft size={16}/>
            </button>
            <div className="grid h-7 w-7 place-items-center rounded-md bg-emerald-500 text-white"><Sparkles size={14}/></div>
            <div className="font-heading text-sm font-semibold">Syllox AI</div>
          </div>
          <Button size="sm" variant="ghost" onClick={newChat} data-testid="new-chat-btn"><Plus size={14}/></Button>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin p-2">
          {sessions.length === 0 ? (
            <div className="p-4 text-center text-xs text-zinc-500">Your chats appear here.</div>
          ) : sessions.map((s) => (
            <div key={s.session_id} className={`group flex items-center gap-1 rounded-lg px-2 py-1.5 ${s.session_id === sessionId ? "bg-emerald-50 dark:bg-emerald-500/10" : "hover:bg-zinc-100 dark:hover:bg-zinc-800"}`}>
              <button onClick={() => setSessionId(s.session_id)} className="flex-1 truncate text-left text-xs" data-testid={`ai-session-${s.session_id}`}>{s.title || "Untitled"}</button>
              <button onClick={() => removeSession(s.session_id)} className="opacity-0 group-hover:opacity-100"><Trash2 size={12} className="text-zinc-400 hover:text-red-500"/></button>
            </div>
          ))}
        </div>
      </aside>

      <section className="flex min-h-0 flex-col">
        {quota && (
          <div className="flex items-center justify-end gap-2 border-b border-zinc-200 px-4 py-1.5 text-xs dark:border-zinc-800" data-testid="ai-quota-bar">
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 font-medium ${quota.remaining <= 5 ? "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300" : "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"}`}>
              <Sparkles size={11}/> {quota.remaining}/{quota.limit} messages left today
            </span>
          </div>
        )}
        <div
          ref={listRef}
          onScroll={handleListScroll}
          className="flex-1 space-y-3 overflow-y-auto overscroll-contain bg-zinc-50 p-4 scrollbar-thin dark:bg-zinc-950/40"
          data-testid="ai-message-list"
        >
          {messages.length === 0 && !thinking && (
            <div className="grid h-full place-items-center text-center text-zinc-500">
              <div>
                <div className="mx-auto mb-2 grid h-12 w-12 place-items-center rounded-2xl bg-emerald-500/10 text-emerald-600"><Sparkles size={20}/></div>
                <h3 className="font-heading text-lg font-semibold text-zinc-800 dark:text-zinc-200">How can I help today?</h3>
                <p className="mt-1 text-xs">Ask any NEET/JEE doubt. Attach an image if it helps.</p>
              </div>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.message_id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm ${m.role === "user" ? "rounded-br-sm bg-emerald-500 text-white whitespace-pre-wrap" : "rounded-bl-sm bg-white text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100"}`}
                   data-testid={`ai-msg-${m.role}`}>
                {m.role === "assistant" ? (
                  <MarkdownMessage content={m.content} />
                ) : (
                  m.content
                )}
              </div>
            </div>
          ))}
          {thinking && (
            <div className="flex justify-start" data-testid="ai-typing">
              <div className="rounded-2xl rounded-bl-sm bg-white px-3.5 py-2 dark:bg-zinc-800">
                <span className="inline-flex gap-1">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-zinc-400" style={{animationDelay:"0ms"}}/>
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-zinc-400" style={{animationDelay:"150ms"}}/>
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-zinc-400" style={{animationDelay:"300ms"}}/>
                </span>
              </div>
            </div>
          )}
        </div>

        {image && (
          <div className="flex items-center gap-2 border-t border-zinc-200 px-4 py-2 dark:border-zinc-800">
            <img src={image} alt="" className="h-12 w-12 rounded object-cover"/>
            <span className="text-xs text-zinc-500">Image attached</span>
            <button onClick={() => setImage(null)} className="ml-auto" data-testid="remove-image-btn"><X size={14}/></button>
          </div>
        )}

        <form onSubmit={send} className="flex items-center gap-2 border-t border-zinc-200 p-3 dark:border-zinc-800">
          <label className="grid h-9 w-9 cursor-pointer place-items-center rounded-md border border-zinc-200 hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800" data-testid="ai-image-upload">
            <ImageIcon size={16}/>
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={pickImage} className="hidden"/>
          </label>
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Ask Syllox AI…" disabled={thinking} data-testid="ai-input"/>
          <Button type="submit" disabled={!text.trim() || thinking} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="ai-send-btn">
            <Send size={16}/>
          </Button>
        </form>
      </section>
    </div>
  );
}
